/**
 * Year6CertificatesPage — /year6/:slug/certificates
 *
 * Teacher edits awards + messages BEFORE downloading the Memory Book.
 * UX flow: paste class list (30s) → auto-assign → tweak 2-5 students → done.
 *
 *   Left  (mobile + desktop): Roster textarea + scrollable student list
 *   Right (desktop only):     Sticky simple preview card (validation, not replication)
 *   Bottom (sticky):          Save / Generate Memory Book bar
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  GraduationCap, Users, Eye, ArrowLeft, ArrowRight, Sparkle,
  ArrowsClockwise, Trophy, BookOpen, Check, Pencil, FloppyDisk,
} from '@phosphor-icons/react';
import Year6GenerateModal from '../components/Year6GenerateModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const THEME_BY_AWARD = {
  comedian: 'gold', kindness: 'rose', creative: 'purple', leader: 'gold',
  athlete: 'green', scholar: 'blue', peacemaker: 'rose', encourager: 'rose',
  imagination: 'purple', artist: 'purple', scientist: 'blue', musician: 'purple',
  writer: 'blue', thinker: 'blue', loyal: 'rose', brave: 'gold',
  hardworker: 'gold', bookworm: 'blue', tech: 'blue', team_player: 'green',
  smile: 'gold', helper: 'green', performer: 'purple', explorer: 'green',
};
const THEME_BAR = {
  gold: '#E5C26B', blue: '#6FA9DC', purple: '#B084F5',
  green: '#7BC57F', rose: '#F5A6BB',
};
const THEME_INK = {
  gold: '#9F7517', blue: '#1A4D93', purple: '#522D9F',
  green: '#1A732F', rose: '#A52E51',
};

export default function Year6CertificatesPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [eventId, setEventId] = useState(null);
  const [data, setData] = useState(null); // {students, awards, school_name, ...}
  const [loading, setLoading] = useState(true);
  const [rosterBlob, setRosterBlob] = useState('');
  const [savingRoster, setSavingRoster] = useState(false);
  const [activeStudentId, setActiveStudentId] = useState(null);
  const [savedTick, setSavedTick] = useState({}); // student_id -> timestamp for tick
  const [generateOpen, setGenerateOpen] = useState(false);
  const [firstPhotoUrl, setFirstPhotoUrl] = useState(null);
  const debounceRefs = useRef({}); // per-student debounce timers

  // Cleanup debounce timers on unmount to avoid stale POSTs after navigate-away
  useEffect(() => {
    const refs = debounceRefs.current;
    return () => { Object.values(refs).forEach((t) => t && clearTimeout(t)); };
  }, []);

  // Resolve slug → event_id + try to get the first event photo for the cover preview
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        if (cancelled) return;
        setEventId(r.data.event_id);
        // Pull first photo URL for the Wow Moment cover preview (cheap, optional)
        try {
          const pv = await axios.get(`${API}/events/${r.data.event_id}/year6/parent-view-data`);
          const first = pv?.data?.photo_urls?.[0];
          if (!cancelled && first) setFirstPhotoUrl(first);
        } catch { /* ignore */ }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Load certificates payload
  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/events/${eventId}/year6/certificates`);
        if (cancelled) return;
        setData(r.data);
        if ((r.data.students || []).length && !activeStudentId) {
          setActiveStudentId(r.data.students[0].student_id);
        }
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [eventId, activeStudentId]);

  const students = data?.students || [];
  const awards = data?.awards || [];
  const activeStudent = useMemo(
    () => students.find((s) => s.student_id === activeStudentId) || students[0] || null,
    [students, activeStudentId]
  );

  const submitRoster = async () => {
    if (!eventId || savingRoster) return;
    setSavingRoster(true);
    try {
      const r = await axios.post(`${API}/events/${eventId}/year6/roster`, { names_blob: rosterBlob });
      setData((prev) => ({ ...(prev || {}), students: r.data.students || [] }));
      if ((r.data.students || []).length) {
        setActiveStudentId(r.data.students[0].student_id);
      }
      toast.success(`${r.data.count} pupil${r.data.count === 1 ? '' : 's'} added — awards auto-assigned`);
      setRosterBlob('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save roster');
    } finally {
      setSavingRoster(false);
    }
  };

  const updateStudentLocal = (sid, patch) => {
    setData((prev) => {
      if (!prev) return prev;
      const next = { ...prev, students: prev.students.map((s) => (s.student_id === sid ? { ...s, ...patch } : s)) };
      return next;
    });
  };

  const persistOverride = async (sid, payload) => {
    try {
      const r = await axios.post(`${API}/events/${eventId}/year6/certificate`, { student_id: sid, ...payload });
      if (r.data?.student) {
        updateStudentLocal(sid, r.data.student);
        setSavedTick((t) => ({ ...t, [sid]: Date.now() }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    }
  };

  const handleAwardChange = (sid, awardId) => {
    const award = awards.find((a) => a.id === awardId);
    if (!award) return;
    const student = students.find((s) => s.student_id === sid);
    // Optimistic local update
    updateStudentLocal(sid, {
      award_id: awardId, award_emoji: award.emoji, award_title: award.title,
      default_message: award.default_message,
      final_message: student?.custom_message?.trim() ? student.custom_message : award.default_message,
      is_overridden: true,
    });
    persistOverride(sid, { award_id: awardId });
  };

  const handleMessageChange = (sid, msg) => {
    updateStudentLocal(sid, {
      custom_message: msg,
      final_message: msg.trim() ? msg : (students.find((s) => s.student_id === sid)?.default_message || ''),
      is_overridden: true,
    });
    if (debounceRefs.current[sid]) clearTimeout(debounceRefs.current[sid]);
    debounceRefs.current[sid] = setTimeout(() => persistOverride(sid, { custom_message: msg }), 800);
  };

  const handleReset = async (sid) => {
    try {
      const r = await axios.delete(`${API}/events/${eventId}/year6/certificate/${sid}`);
      if (r.data?.student) {
        updateStudentLocal(sid, r.data.student);
        toast.success('Reset to default');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reset');
    }
  };

  const openPreview = () => {
    if (!data?.memory_book_purchased) {
      toast.message('Memory Book not unlocked yet — preview is only available after £99 unlock');
      return;
    }
    window.open(`${API}/events/${eventId}/year6/memory-book/download`, '_blank');
  };

  if (loading) {
    return <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center" data-testid="year6-cert-loading">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-white pb-32" data-testid="year6-certificates-page">
      <div className="max-w-6xl mx-auto px-5 pt-8">
        {/* Header */}
        <button onClick={() => navigate(`/year6/${slug}/control`)} className="text-white/55 hover:text-white text-xs inline-flex items-center gap-1.5 mb-4" data-testid="year6-cert-back">
          <ArrowLeft size={12} weight="bold" /> Back to teacher controls
        </button>
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-300/10 border border-amber-300/30 text-amber-200 text-[10px] font-black uppercase tracking-[0.18em] mb-3">
          <Trophy size={11} weight="fill" /> Class Awards
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-black leading-tight mb-1.5" data-testid="year6-cert-title">
          🏆 Class Awards & Certificates
        </h1>
        <p className="text-white/60 text-sm mb-2 leading-relaxed">
          Each child gets a personalised certificate in the final Memory Book.
        </p>
        <p className="text-white/45 text-[12.5px] mb-6 leading-relaxed">
          Everything has been automatically assigned — you can tweak anything below.
        </p>

        {/* Empty state — no roster yet */}
        {students.length === 0 && (
          <div className="rounded-2xl border border-amber-300/30 bg-amber-300/5 p-5 mb-6" data-testid="year6-cert-empty">
            <div className="flex items-start gap-3 mb-3">
              <div className="shrink-0 w-9 h-9 rounded-xl bg-amber-300/15 flex items-center justify-center text-amber-200">
                <Users size={18} weight="fill" />
              </div>
              <div>
                <p className="text-white font-bold text-base">Add your class list to generate certificates for every pupil</p>
                <p className="text-white/55 text-xs mt-0.5">💡 Tip: Paste directly from Excel or your class list — newlines, tabs and commas all work.</p>
              </div>
            </div>
            <textarea
              value={rosterBlob}
              onChange={(e) => setRosterBlob(e.target.value)}
              rows={6}
              placeholder={"Oliver Smith\nOlivia Jones\nJack Brown\nMaya Patel\n…"}
              className="w-full px-3.5 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-white/25 text-sm font-mono leading-relaxed focus:outline-none focus:border-amber-300/50"
              data-testid="year6-cert-roster-textarea"
            />
            <div className="flex items-center justify-between mt-3">
              <p className="text-white/40 text-[11px]">{_estimateNames(rosterBlob)} pupil{_estimateNames(rosterBlob) === 1 ? '' : 's'} detected</p>
              <button
                onClick={submitRoster}
                disabled={savingRoster || !rosterBlob.trim()}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-black text-white shadow-lg disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
                data-testid="year6-cert-roster-submit"
              >
                {savingRoster ? 'Saving…' : <>Save class list <ArrowRight size={14} weight="bold" /></>}
              </button>
            </div>
          </div>
        )}

        {students.length > 0 && (
          <>
            {/* Bulk actions bar */}
            <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="year6-cert-bulk-bar">
              <button
                onClick={() => { setRosterBlob(students.map((s) => s.name).join('\n')); setData((p) => ({ ...(p || {}), students: [] })); }}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-bold border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-white/85"
                data-testid="year6-cert-edit-roster"
              >
                <Pencil size={12} weight="bold" /> Edit class list
              </button>
              <button
                onClick={async () => {
                  // Re-roll all overrides off → re-auto-assign
                  await Promise.all(students.filter((s) => s.is_overridden).map((s) => axios.delete(`${API}/events/${eventId}/year6/certificate/${s.student_id}`)));
                  const r = await axios.get(`${API}/events/${eventId}/year6/certificates`);
                  setData(r.data);
                  toast.success('Awards reset to auto-assigned');
                }}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-bold border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-white/85"
                data-testid="year6-cert-reset-all"
              >
                <ArrowsClockwise size={12} weight="bold" /> Reset all to defaults
              </button>
              <button
                onClick={openPreview}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-bold border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-white/85"
                data-testid="year6-cert-preview-book"
              >
                <Eye size={12} weight="fill" /> Preview Memory Book
              </button>
              <span className="text-white/40 text-[11px] ml-auto" data-testid="year6-cert-counter">
                {students.length} pupil{students.length === 1 ? '' : 's'} · {students.filter((s) => s.is_overridden).length} personalised{students.filter((s) => s.is_overridden).length > 0 ? ' \u2728' : ''}
              </span>
            </div>

            {/* Two-column: list + sticky preview */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
              {/* LEFT — student list */}
              <div className="space-y-3" data-testid="year6-cert-list">
                {students.map((s) => (
                  <StudentRow
                    key={s.student_id}
                    student={s}
                    awards={awards}
                    onAwardChange={handleAwardChange}
                    onMessageChange={handleMessageChange}
                    onReset={handleReset}
                    onActivate={() => setActiveStudentId(s.student_id)}
                    isActive={activeStudent?.student_id === s.student_id}
                    savedAt={savedTick[s.student_id]}
                  />
                ))}
              </div>

              {/* RIGHT — preview card (desktop sticky) */}
              <div className="hidden lg:block">
                <div className="sticky top-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-400/12 border border-emerald-400/30 text-emerald-300 text-[9px] font-black uppercase tracking-[0.16em]">
                      <Eye size={10} weight="fill" /> Preview
                    </span>
                    <span className="text-[10px] font-bold text-white/55">what parents will receive</span>
                  </div>
                  {activeStudent && <CertificatePreviewCard student={activeStudent} schoolName={data?.school_name} />}
                  <p className="text-[11px] text-white/40 italic mt-3 text-center leading-relaxed" data-testid="year6-cert-preview-reassurance">
                    Final PDF includes premium design, print-ready quality, and a certificate for every child.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Sticky bottom action bar */}
      {students.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 bg-[#0F1014]/95 backdrop-blur-xl px-5 py-3.5" data-testid="year6-cert-sticky-bar">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
            <p className="text-[11px] text-white/55 hidden sm:block">
              <Check size={12} weight="bold" className="inline text-emerald-400 mb-0.5" /> Auto-saved
            </p>
            <div className="flex items-center gap-2 ml-auto">
              <button onClick={openPreview} className="hidden sm:inline-flex items-center gap-1.5 px-4 py-3 rounded-xl text-[13px] font-bold border border-white/15 bg-white/[0.04] hover:bg-white/[0.08]" data-testid="year6-cert-bottom-preview">
                <Eye size={13} weight="fill" /> Preview
              </button>
              <button
                onClick={() => {
                  if (!data?.memory_book_purchased) {
                    toast.message('Memory Book is locked — unlock for £99 first');
                    return;
                  }
                  setGenerateOpen(true);
                }}
                className={`relative inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-[14.5px] font-black text-white ${data?.memory_book_purchased ? '' : 'opacity-60'}`}
                style={{
                  background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
                  boxShadow: data?.memory_book_purchased
                    ? '0 12px 36px -10px rgba(139,92,246,0.55), 0 0 0 1px rgba(255,255,255,0.06) inset'
                    : '0 4px 14px -4px rgba(0,0,0,0.4)',
                }}
                data-testid="year6-cert-bottom-generate"
              >
                <BookOpen size={15} weight="fill" /> Create Memory Book 🎓
              </button>
            </div>
          </div>
        </div>
      )}

      <Year6GenerateModal
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        eventId={eventId}
        slug={slug}
        schoolName={data?.school_name}
        students={students}
        firstPhotoUrl={firstPhotoUrl}
      />
    </div>
  );
}

/* ---------- helpers ---------- */
function _estimateNames(blob) {
  if (!blob) return 0;
  const split = blob.split(/[\n\t]+|,\s*|  +/).map((s) => s.trim()).filter(Boolean);
  return new Set(split.map((s) => s.toLowerCase())).size;
}

/* ---------- Student Row ---------- */
function StudentRow({ student, awards, onAwardChange, onMessageChange, onReset, onActivate, isActive, savedAt }) {
  const [showCheck, setShowCheck] = useState(false);
  useEffect(() => {
    if (!savedAt) return;
    setShowCheck(true);
    const t = setTimeout(() => setShowCheck(false), 1200);
    return () => clearTimeout(t);
  }, [savedAt]);
  const themeKey = THEME_BY_AWARD[student.award_id] || 'gold';
  const themeBar = THEME_BAR[themeKey];
  const initials = (student.name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?';
  const currentAward = awards.find((a) => a.id === student.award_id);

  return (
    <div
      onClick={onActivate}
      className={`rounded-2xl border p-4 transition-all cursor-pointer ${isActive ? 'border-amber-300/55 bg-white/[0.05] shadow-lg shadow-amber-500/5' : 'border-white/8 bg-white/[0.025] hover:bg-white/[0.04]'}`}
      data-testid={`year6-cert-row-${student.student_id}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-black text-[15px] relative"
          style={{ background: `linear-gradient(135deg, ${themeBar}40, ${themeBar}18)`, border: `1.5px solid ${themeBar}80`, color: '#fff' }}
        >
          <span className="opacity-95" data-testid={`year6-cert-initials-${student.student_id}`}>{initials}</span>
          <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#0F1014] border border-white/15 flex items-center justify-center text-[13px]">
            {student.award_emoji}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-[15px] leading-tight">{student.name}</p>
          <p className="text-white/45 text-[11px] mt-0.5">{student.is_overridden ? '✨ Personalised by you' : 'Auto-assigned · ready to tweak'}</p>
        </div>
        {showCheck && (
          <span className="text-emerald-300 text-[10px] inline-flex items-center gap-1" data-testid={`year6-cert-saved-${student.student_id}`}>
            <FloppyDisk size={11} weight="fill" /> Saved
          </span>
        )}
      </div>

      <label className="block mb-3">
        <span className="text-[10px] font-black uppercase tracking-wider text-white/45 mb-1.5 block">Award</span>
        <select
          value={student.award_id}
          onChange={(e) => onAwardChange(student.student_id, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          className="w-full px-3 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-white text-sm focus:outline-none focus:border-amber-300/50"
          data-testid={`year6-cert-award-${student.student_id}`}
        >
          {awards.map((a) => (
            <option key={a.id} value={a.id} className="bg-neutral-900">{a.emoji} {a.title}</option>
          ))}
        </select>
        {currentAward && (
          <p className="text-white/45 text-[11px] mt-1.5 italic leading-snug" data-testid={`year6-cert-award-hint-${student.student_id}`}>
            {currentAward.default_message.split(/[—–\-]/)[0].trim().slice(0, 70)}{currentAward.default_message.length > 70 ? '…' : ''}
          </p>
        )}
      </label>

      <label className="block">
        <span className="text-[10px] font-black uppercase tracking-wider text-white/45 mb-1.5 block flex items-center gap-1.5">
          Personalised message
          <span className="text-[9px] text-white/30 normal-case font-normal tracking-normal italic">editable by you</span>
        </span>
        <textarea
          value={student.custom_message || student.default_message || ''}
          onChange={(e) => onMessageChange(student.student_id, e.target.value)}
          onClick={(e) => e.stopPropagation()}
          rows={2}
          className="w-full px-3 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-white text-sm leading-snug focus:outline-none focus:border-amber-300/50"
          placeholder="Personalise this message…"
          data-testid={`year6-cert-msg-${student.student_id}`}
        />
      </label>

      {student.is_overridden && (
        <button
          onClick={(e) => { e.stopPropagation(); onReset(student.student_id); }}
          className="mt-2 text-[11px] text-white/45 hover:text-white/75 underline underline-offset-2 inline-flex items-center gap-1"
          data-testid={`year6-cert-reset-${student.student_id}`}
        >
          <ArrowsClockwise size={11} weight="bold" /> Reset to default
        </button>
      )}
    </div>
  );
}

/* ---------- Simple Preview Card (validation, not replication) ---------- */
function CertificatePreviewCard({ student, schoolName }) {
  const themeKey = THEME_BY_AWARD[student.award_id] || 'gold';
  const bar = THEME_BAR[themeKey];
  const ink = THEME_INK[themeKey];
  const finalMsg = student.final_message || student.default_message || '';

  return (
    <div
      className="rounded-2xl text-neutral-900 overflow-hidden aspect-[3/4] relative"
      style={{
        background: '#fafaf7',
        boxShadow: '0 24px 50px -20px rgba(0,0,0,0.55), 0 4px 14px -4px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(0,0,0,0.04)',
      }}
      data-testid="year6-cert-preview-card"
    >
      {/* paper grain */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none mix-blend-multiply opacity-[0.06]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 30%, #000 1px, transparent 1px), radial-gradient(circle at 70% 60%, #000 1px, transparent 1.4px), radial-gradient(circle at 40% 80%, #000 0.8px, transparent 1.2px)',
          backgroundSize: '4px 4px, 5px 5px, 6px 6px',
        }}
      />
      <div className="h-3 relative z-10" style={{ background: bar }} />
      <div className="px-6 py-7 flex flex-col h-[calc(100%-24px)] relative z-10">
        <p className="text-[10px] font-black uppercase tracking-[0.22em] text-center mb-3" style={{ color: ink }}>Certificate of Achievement</p>
        <div className="text-center text-3xl mb-3" data-testid="year6-cert-preview-emoji">{student.award_emoji}</div>
        <p className="text-center text-[11px] italic text-neutral-500 mb-1" style={{ fontFamily: 'Georgia, serif' }}>Presented to</p>
        <p className="text-center text-[26px] font-black leading-tight mb-2" style={{ fontFamily: 'Georgia, serif' }} data-testid="year6-cert-preview-name">{student.name}</p>
        <p className="text-center text-[15px] italic mb-3" style={{ fontFamily: 'Georgia, serif', color: ink }} data-testid="year6-cert-preview-title">"{student.award_title}"</p>
        <p className="text-center text-[11px] leading-relaxed text-neutral-700 px-2 flex-1" style={{ fontFamily: 'Georgia, serif' }} data-testid="year6-cert-preview-message">{finalMsg}</p>
        <div className="border-t border-neutral-200 pt-2 mt-2 text-center">
          <p className="text-[10px] font-bold text-neutral-700" style={{ fontFamily: 'Georgia, serif' }}>{schoolName || 'Your School'}</p>
          <p className="text-[9px] text-neutral-400 mt-0.5">Year 6 · Class of 2026</p>
        </div>
      </div>
      <div className="h-3 relative z-10" style={{ background: bar }} />
    </div>
  );
}
